import React, { useReducer, useMemo, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import reducer, {initialState, LOGGED_IN, LOGGED_OUT} from "./reducer";


// ====================
// Context & Utilities
// ====================

const AuthContext = React.createContext();
export const useAuth = () => useContext(AuthContext);

// Set or clear Knox Token header
export const setAuthorization = (token) => {
  if (!token) delete axios.defaults.headers.common["Authorization"];
  else axios.defaults.headers.common["Authorization"] = `Token ${token}`; // Knox uses "Token"
};

// ====================
// Provider
// ====================

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [authLoading, setAuthLoading] = useState(true); // Add loading state

  // Restore user & token from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem("authToken");
    const storedUser = localStorage.getItem("authUser");

    if (storedToken && storedUser) {
      const user = JSON.parse(storedUser);
      setAuthorization(storedToken);
      dispatch({ type: LOGGED_IN, user, token: storedToken });
    }
    setAuthLoading(false); // Auth restoration complete
  }, []);


  // --- Auth Handlers ---
  
  const handleLogin = ({ user, token }) => {
    setAuthorization(token);
    localStorage.setItem("authToken", token);
    localStorage.setItem("authUser", JSON.stringify(user));
    dispatch({ type: LOGGED_IN, user, token });
  };

  const handleLogout = () => {
    setAuthorization(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    dispatch({ type: LOGGED_OUT });
  };


 // --- Axios 401 Interceptor ---

  useEffect(() => {
    const PUBLIC_ROUTES = ["/Login", "/RegisterPage", "/RegisterPage2Col", "/ForgotWelcome", "/EmailPwPage", "/api/gifts/redeem"];

    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const status = err.response?.status;
        const url = err.config?.url || "";

        // Skip logout for public routes or non-401 errors
        if (status !== 401 || PUBLIC_ROUTES.some((path) => url.includes(path))) {
          return Promise.reject(err);
        }

        // Token expired or invalid -> logout
        handleLogout();
        return Promise.reject(err);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []);


 // --- Context Value ---

 const value = useMemo(() => ({ 
    state, 
    handleLogin, 
    handleLogout,
    authLoading  
  }), [state, authLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

{/*

| Problem                    | Cause                                                 | Fix                                        |
| -------------------------- | ----------------------------------------------------- | ------------------------------------------ |
| `401` on first `/register` | Global 401 interceptor was logging out on *every* 401 | Interceptor now **skips public routes**    |
| Token didn’t persist       | Axios header wasn’t always `Token` (Knox format)      | Now always uses `Token ${token}`           |
| No refresh flow            | Knox doesn’t support it                               | We log out on real token expiry (401) only |

*/}